/**
 * Reference Image Preview / Lightbox Fix — shared modal.
 *
 * Used by ProductionContinuityPanel + StoryboardPanel (per-shot thumbs +
 * Continuity References Used header) to open any reference asset (uploaded
 * OR AI-generated) at full size on top of the dark dashboard.
 *
 * Read-only. NO new endpoints, NO new AI calls. Uses the existing
 * `GET /api/assets/:id/stream` endpoint with the same `?auth=<token>`
 * query-string trick the rest of the codebase already uses (since
 * <img> tags cannot send Authorization headers).
 *
 * Controlled component: parent owns `{open, assetId, title}` state and
 * calls `onClose()` to dismiss.
 */
import { useEffect, useRef } from "react";
import { X, ExternalLink } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function buildSrc(assetId) {
    if (!assetId) return "";
    const token = (typeof window !== "undefined")
        ? localStorage.getItem("aivos_token") : null;
    const qs = token ? `?auth=${encodeURIComponent(token)}` : "";
    return `${BACKEND}/api/assets/${assetId}/stream${qs}`;
}

export default function ImageLightbox({ open, assetId, title, onClose }) {
    const closeBtnRef = useRef(null);

    // Esc closes the lightbox.
    useEffect(() => {
        if (!open) return undefined;
        const onKey = (e) => {
            if (e.key === "Escape") {
                e.stopPropagation();
                onClose && onClose();
            }
        };
        window.addEventListener("keydown", onKey);
        // Focus the close button so screen readers + keyboard users land here.
        if (closeBtnRef.current) closeBtnRef.current.focus();
        return () => window.removeEventListener("keydown", onKey);
    }, [open, onClose]);

    if (!open || !assetId) return null;

    const src = buildSrc(assetId);

    return (
        <div
            role="dialog"
            aria-modal="true"
            aria-label={title || "Reference image preview"}
            data-testid="image-lightbox"
            className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4 sm:p-8"
            onClick={onClose}
        >
            {/* Top-right control bar — stop propagation so clicks here
                don't close the modal. */}
            <div
                className="absolute top-3 right-3 flex items-center gap-2"
                onClick={(e) => e.stopPropagation()}
            >
                <a
                    href={src}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="image-lightbox-open-new-tab"
                    className="inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-wider text-zinc-300 hover:text-white border border-zinc-700 hover:border-zinc-500 bg-zinc-950/80 rounded-sm px-2.5 py-1.5 transition"
                    title="Open in new tab"
                >
                    <ExternalLink className="w-3.5 h-3.5" />
                    Open in new tab
                </a>
                <button
                    ref={closeBtnRef}
                    type="button"
                    onClick={onClose}
                    data-testid="image-lightbox-close"
                    className="inline-flex items-center justify-center w-9 h-9 border border-zinc-700 hover:border-zinc-500 bg-zinc-950/80 rounded-sm text-zinc-300 hover:text-white transition"
                    aria-label="Close image preview"
                    title="Close (Esc)"
                >
                    <X className="w-4 h-4" />
                </button>
            </div>

            {/* Title strip (bottom). */}
            {title && (
                <div
                    className="absolute bottom-4 left-1/2 -translate-x-1/2 text-[11px] font-mono uppercase tracking-wider text-zinc-300 bg-zinc-950/80 border border-zinc-800 rounded-sm px-3 py-1.5 max-w-[80vw] truncate"
                    onClick={(e) => e.stopPropagation()}
                    data-testid="image-lightbox-title"
                >
                    {title}
                </div>
            )}

            {/* Image. Clicks on the image itself do NOT close (so users can
                drag-select etc.). The dark backdrop closes on click. */}
            <img
                src={src}
                alt={title || assetId}
                data-testid="image-lightbox-img"
                onClick={(e) => e.stopPropagation()}
                className="max-w-[90vw] max-h-[90vh] object-contain rounded-sm shadow-2xl"
            />
        </div>
    );
}
